/**
 * LegalDocument — renders a structured legal document bundle
 * (see /app/frontend/src/lib/legalCopy/*.js for the schema).
 *
 * Usage:
 *   <LegalDocument docKey="terms" />
 *
 * Renders:
 *   - preamble block (para or contact rows) in a gold-bordered quote
 *   - numbered or untitled sections via LegalSection primitive
 *   - paragraphs and lists inside each section
 *
 * Falls back to the IT copy when the current locale has no translation yet.
 */
import { useLocale } from "@/lib/locale";
import { getLegalCopy } from "@/lib/legalCopy";
import {
    LegalPreamble,
    LegalParagraph,
    LegalSection,
    LegalList,
    LegalContact,
} from "@/components/LegalContent";

export default function LegalDocument({ docKey }) {
    const { locale } = useLocale();
    const doc = getLegalCopy(locale, docKey);

    return (
        <>
            {doc.preamble && doc.preamble.length > 0 && (
                <LegalPreamble>
                    {doc.preamble.map((item, i) => {
                        if (item.type === "contact") {
                            return (
                                <LegalContact
                                    key={i}
                                    label={item.label}
                                    value={item.value}
                                    mailto={item.mailto}
                                />
                            );
                        }
                        return <p key={i}>{item.text}</p>;
                    })}
                </LegalPreamble>
            )}

            {doc.sections.map((section, i) => (
                <LegalSection
                    key={i}
                    number={section.number}
                    title={section.title}
                >
                    {section.blocks.map((block, j) => {
                        if (block.type === "list") {
                            return <LegalList key={j} items={block.items} />;
                        }
                        return <LegalParagraph key={j}>{block.text}</LegalParagraph>;
                    })}
                </LegalSection>
            ))}
        </>
    );
}
